import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateInvoice.scss";

export const CreateInvoice: React.FC = () => {
  const { orderType, id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'common']);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    collectionDate: "",
    receivedDate: "",
    isCollected: false,
    isReceived: false,
    amount: 0,
    orderId: null,
    shipForMeId: null,
    externalInvoiceNumber: "",
    invoiceType: 0,
    receivedInvoiceNumber: "",
  });

  const [loading, setLoading] = useState(false);

  // Set either orderId or shipForMeId based on the route param
  useEffect(() => {
    if (orderType === "order") {
      setFormData((prev) => ({
        ...prev,
        orderId: parseInt(id),
        shipForMeId: null,
      }));
    } else if (orderType === "ship") {
      setFormData((prev) => ({
        ...prev,
        shipForMeId: parseInt(id),
        orderId: null,
      }));
    }
  }, [orderType, id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    let fieldValue: any = type === "checkbox" ? checked : value;
    fieldValue = name.includes("invoiceType") ? Number(value) : fieldValue;

    setFormData({
      ...formData,
      [name]: fieldValue,
    });
  };

  const submitInvoice = async () => {
    setLoading(true);
    const payload = {
      ...formData,
      receivedDate: formData.receivedDate === "" ? null : formData.receivedDate,
      collectionDate:
        formData.collectionDate === "" ? null : formData.collectionDate,
    };
    try {
      const response = await adminApi.call('/invoice', 'POST', payload);

      if (!response.success) {
        showErrorToast(response.error || t('invoices:messages.createError'));
        return;
      }

      showSuccessToast(t('invoices:messages.createSuccess'));
      setFormData({
        collectionDate: "",
        receivedDate: "",
        isCollected: false,
        isReceived: false,
        amount: 0,
        orderId: orderType === "order" ? parseInt(id!) : null,
        shipForMeId: orderType === "ship" ? parseInt(id!) : null,
        externalInvoiceNumber: "",
        receivedInvoiceNumber: "",
        invoiceType: 0
      });
    } catch (error: any) {
      showErrorToast(t('invoices:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('invoices:breadcrumbs.invoices'), path: '/invoices' },
    { label: t('invoices:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('invoices:createInvoice')}
      subtitle={t('invoices:subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="modern-form">
        <div className="form-grid">
          {/* Collection Date */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.collectionDate')}</label>
            <input
              type="date"
              className="form-control"
              name="collectionDate"
              value={formData.collectionDate}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.collectionDate')}
            />
          </div>

          {/* Received Date */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.receivedDate')}</label>
            <input
              type="date"
              className="form-control"
              name="receivedDate"
              value={formData.receivedDate}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.receivedDate')}
            />
          </div>

          {/* IsCollected */}
          <div className="form-group checkbox-group">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="isCollected"
                id="isCollected"
                checked={formData.isCollected}
                onChange={handleInputChange}
              />
              <label className="form-check-label" htmlFor="isCollected">
                {t('invoices:fields.isCollected')}
              </label>
            </div>
          </div>

          {/* IsReceived */}
          <div className="form-group checkbox-group">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="isReceived"
                id="isReceived"
                checked={formData.isReceived}
                onChange={handleInputChange}
              />
              <label className="form-check-label" htmlFor="isReceived">
                {t('invoices:fields.isReceived')}
              </label>
            </div>
          </div>

          {/* Amount */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.amount')}</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.amount')}
            />
          </div>

          {/* External Invoice Number */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.externalInvoiceNumber')}</label>
            <input
              type="text"
              className="form-control"
              name="externalInvoiceNumber"
              value={formData.externalInvoiceNumber}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.externalInvoiceNumber')}
            />
          </div>

          {/* Received Invoice Number */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.receivedInvoiceNumber')}</label>
            <input
              type="text"
              className="form-control"
              name="receivedInvoiceNumber"
              value={formData.receivedInvoiceNumber}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.receivedInvoiceNumber')}
            />
          </div>

          {/* Show Order ID or Ship For Me ID */}
          {orderType === "order" && (
            <div className="form-group">
              <label className="form-label">{t('invoices:fields.orderId')}</label>
              <input
                type="number"
                className="form-control"
                name="orderId"
                value={formData.orderId || ""}
                readOnly
              />
            </div>
          )}

          {orderType === "ship" && (
            <div className="form-group">
              <label className="form-label">{t('invoices:fields.shipForMeId')}</label>
              <input
                type="number"
                className="form-control"
                name="shipForMeId"
                value={formData.shipForMeId || ""}
                readOnly
              />
            </div>
          )}

          {/* Invoice Type */}
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.invoiceType')}</label>
            <select
              className="form-control"
              name="invoiceType"
              value={formData.invoiceType}
              onChange={handleInputChange}
            >
              <option value={0}>{t('invoices:invoiceTypeOptions.in')}</option>
              <option value={1}>{t('invoices:invoiceTypeOptions.out')}</option>
            </select>
          </div>
        </div>
        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitInvoice}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('invoices:buttons.submitting') : t('invoices:buttons.submit')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
